/**
 * The "Start with" pool.
 *
 * These used to be generated per visit by asking Copilot to read the user's
 * Microsoft 365 context, which cost a full model round trip before an empty
 * chat could finish painting. The list is static now: a deliberately wide
 * pool, of which the empty state shows three picked at random, so the panel
 * still feels different between visits without waiting on anything.
 */
export interface StarterSuggestion {
  text: string;
  tag: string;
}

/** How many of the pool the empty state shows at once. */
export const STARTER_SUGGESTION_COUNT = 3;

export const STARTER_SUGGESTIONS: StarterSuggestion[] = [
  // Accounts and customers
  { text: "Catch me up on the latest activity for a customer.", tag: "Accounts" },
  { text: "Which of my accounts have gone quiet for more than two weeks?", tag: "Accounts" },
  { text: "Draft a short status update I can send to a customer this week.", tag: "Accounts" },
  { text: "Summarize everything we know about an account before my next call.", tag: "Accounts" },

  // Meetings
  { text: "Summarize the important decisions and action items from my recent meetings.", tag: "Meetings" },
  { text: "Prepare me for my next meeting: who is attending and what is open.", tag: "Meetings" },
  { text: "List the commitments I made in meetings that are still unfinished.", tag: "Meetings" },
  { text: "Write follow-up notes from my last meeting that I can send out.", tag: "Meetings" },

  // Documents
  { text: "Find the current SLA or procedure document in SharePoint.", tag: "Documents" },
  { text: "Which documents in my team's site changed in the last week?", tag: "Documents" },
  { text: "Summarize a long document into the points that actually matter.", tag: "Documents" },
  { text: "Compare the latest version of a document with the one before it.", tag: "Documents" },

  // Mail and Teams threads
  { text: "Summarize the latest email and Teams conversation about a customer issue.", tag: "Threads" },
  { text: "Which emails am I still expected to answer?", tag: "Threads" },
  { text: "Draft a reply to the last message in an important thread.", tag: "Threads" },
  { text: "Find the thread where a decision was made and show me the reasoning.", tag: "Threads" },

  // Daily focus
  { text: "What important items from Microsoft 365 need my attention today?", tag: "Daily" },
  { text: "Plan my day around what is already on my calendar.", tag: "Daily" },
  { text: "What slipped through the cracks while I was away?", tag: "Daily" },
  { text: "Give me a short end-of-week summary of what I worked on.", tag: "Daily" },

  // Projects and delivery
  { text: "Where does a project stand and what is blocking it?", tag: "Projects" },
  { text: "List the open risks and owners across my active projects.", tag: "Projects" },
  { text: "Build a status report for a project from its recent activity.", tag: "Projects" },

  // People and teams
  { text: "Who is the right person to ask about a given system or process?", tag: "People" },
  { text: "Summarize what my team has been working on this week.", tag: "People" },

  // Writing help
  { text: "Rewrite a message so it is shorter and clearer.", tag: "Writing" },
  { text: "Turn my rough notes into a clean summary I can share.", tag: "Writing" },
  { text: "Draft an agenda for a meeting I need to run.", tag: "Writing" },

  // Research and analysis
  { text: "Explain how a process works based on our internal documentation.", tag: "Research" },
  { text: "Pull the numbers from a recent report and explain the trend.", tag: "Research" },
];

/**
 * `count` distinct suggestions, chosen at random.
 *
 * Draws from a copy of the pool and removes each pick, so the same
 * suggestion never shows up twice in one draw.
 */
export function pickStarterSuggestions(
  count = STARTER_SUGGESTION_COUNT,
  pool: StarterSuggestion[] = STARTER_SUGGESTIONS,
): StarterSuggestion[] {
  const remaining = [...pool];
  const take = Math.min(count, remaining.length);
  const picked: StarterSuggestion[] = [];
  while (picked.length < take) {
    const [chosen] = remaining.splice(
      Math.floor(Math.random() * remaining.length),
      1,
    );
    if (chosen) picked.push(chosen);
  }
  return picked;
}
